import React, { useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import Button from '@material-ui/core/Button'
import { NeutralChip } from 'components/student-insights/retention-status-chip'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import LinearProgress from '@material-ui/core/LinearProgress'
import Typography from '@material-ui/core/Typography'
import Alert from '@material-ui/lab/Alert'
import Dialog from 'components/dialog'
import CheckIcon from 'components/material-symbol/icons/Check'
import { useDialogTitleStyles } from 'components/dialog/use-dialog-title-styles'
import pluralize from 'lib/pluralize'
import { RULE_COPY, describeRule, describeSchedule } from './constants'
import { usePreviewOutreachRule } from './api-hooks'
import useStyles from './styles'

const matchSummary = ({ eligibleCount, skippedCount, students }) => {
  if (eligibleCount === 0) {
    return 'No students match right now. When one does, they’ll receive this message on the next scheduled send.'
  }
  const count = students.length
  const parts = [
    `Up to ${count} ${pluralize(
      count,
      'student',
    )} will receive this message on the next send.`,
  ]
  const later = eligibleCount - count
  if (later > 0) {
    parts.push(`Up to ${later} more could receive it on later sends.`)
  }
  if (skippedCount > 0) {
    parts.push(
      `${skippedCount} ${pluralize(
        skippedCount,
        'other',
      )} will be skipped, for example because they already booked or you messaged them recently.`,
    )
  }
  return parts.join(' ')
}

export function ApprovalDialogView({
  open,
  trigger,
  delayDays,
  reapproval,
  sendSchedule,
  holdoutPercent,
  preview,
  loading,
  previewFailed,
  saving,
  error,
  onRetryPreview,
  onCancel,
  onConfirm,
}) {
  const classes = useStyles()
  const titleClasses = useDialogTitleStyles()
  const [first] = preview?.students || []

  return (
    <Dialog
      isOpen={open}
      onClose={onCancel}
      fullWidth
      maxWidth="sm"
      showCloseButton
      disableClose={saving}
      classes={{ paper: titleClasses.dialogPaper }}
    >
      {() => (
        <>
          <DialogTitle
            disableTypography
            classes={{
              root: clsx(titleClasses.titleRoot, classes.approvalTitle),
            }}
          >
            <Typography component="h2" className={titleClasses.title}>
              {reapproval ? 'Approve new timing' : 'Turn on'}:{' '}
              {RULE_COPY[trigger].title}
            </Typography>
            <Typography className={titleClasses.bodyText}>
              {describeRule(trigger, delayDays)} The message comes from you in
              PGA Coach. If they don’t have the app, they’ll receive it by email
              and can reply.
            </Typography>
          </DialogTitle>
          <DialogContent
            className={clsx(titleClasses.content, classes.approvalContent)}
          >
            {previewFailed ? (
              <Alert
                severity="warning"
                action={
                  <Button color="inherit" size="small" onClick={onRetryPreview}>
                    Try Again
                  </Button>
                }
              >
                We couldn’t load who would get this message.
              </Alert>
            ) : loading || !preview ? (
              <LinearProgress color="primary" />
            ) : (
              <>
                <Typography className={classes.dialogHint}>
                  {matchSummary(preview)}
                </Typography>
                {!preview.conversationsChecked ? (
                  <Alert severity="info" className={classes.dialogAlert}>
                    We couldn’t check recent chats for everyone listed. Before
                    each send, PGA Coach checks again and skips anyone you
                    messaged recently.
                  </Alert>
                ) : null}
                {first ? (
                  <>
                    <Typography className={classes.previewLabel}>
                      What {first.student.firstName} would get
                    </Typography>
                    <Typography
                      className={classes.previewMessage}
                      data-testid="approval-preview-message"
                    >
                      {first.body}
                    </Typography>
                    <Typography className={classes.previewLabel}>
                      Next send
                    </Typography>
                    <div className={classes.recipients}>
                      {preview.students.map(({ student }) => (
                        <NeutralChip key={student.id}>
                          {`${student.firstName} ${student.lastName}`}
                        </NeutralChip>
                      ))}
                    </div>
                  </>
                ) : null}
                <Typography className={classes.finePrint}>
                  Messages send on {describeSchedule(sendSchedule)}, with a
                  limit of {preview.dailyCap} automated messages per day.
                  Students you’ve recently followed up with are skipped. To help
                  measure whether follow-ups lead to bookings, about{' '}
                  {holdoutPercent}% of eligible students are randomly skipped.
                </Typography>
                <Typography
                  className={clsx(classes.finePrint, classes.finePrintBreak)}
                >
                  You can turn this automation off anytime.
                </Typography>
              </>
            )}
            {error ? (
              <Alert severity="warning" style={{ marginTop: 16 }}>
                {error}
              </Alert>
            ) : null}
          </DialogContent>
          <DialogActions className={titleClasses.actions}>
            <Button
              onClick={onCancel}
              color="primary"
              size="large"
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={onConfirm}
              color="primary"
              variant="contained"
              size="large"
              disableElevation
              startIcon={<CheckIcon />}
              disabled={loading || !preview || saving}
              data-testid="approval-confirm"
            >
              {reapproval ? 'Approve' : 'Turn On'}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}

ApprovalDialogView.propTypes = {
  open: PropTypes.bool.isRequired,
  trigger: PropTypes.oneOf(Object.keys(RULE_COPY)).isRequired,
  delayDays: PropTypes.number.isRequired,
  reapproval: PropTypes.bool,
  sendSchedule: PropTypes.string.isRequired,
  holdoutPercent: PropTypes.number.isRequired,
  preview: PropTypes.shape({
    eligibleCount: PropTypes.number.isRequired,
    skippedCount: PropTypes.number.isRequired,
    dailyCap: PropTypes.number.isRequired,
    conversationsChecked: PropTypes.bool.isRequired,
    students: PropTypes.arrayOf(PropTypes.object).isRequired,
  }),
  loading: PropTypes.bool,
  previewFailed: PropTypes.bool,
  saving: PropTypes.bool,
  error: PropTypes.string,
  onRetryPreview: PropTypes.func,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
}

export default function ApprovalDialog({
  rule,
  sendSchedule,
  holdoutPercent,
  saving,
  error,
  onCancel,
  onConfirm,
}) {
  const [loadPreview, { called, data, loading }] = usePreviewOutreachRule()
  const { trigger, delayDays, previousDelayDays } = rule
  const preview = data?.previewOutreachRule
  const requestPreview = useCallback(
    () => loadPreview({ variables: { trigger, delayDays } }),
    [loadPreview, trigger, delayDays],
  )

  useEffect(() => {
    requestPreview()
  }, [requestPreview])

  return (
    <ApprovalDialogView
      open
      trigger={trigger}
      delayDays={delayDays}
      sendSchedule={sendSchedule}
      holdoutPercent={holdoutPercent}
      preview={preview}
      loading={loading}
      reapproval={previousDelayDays != null}
      previewFailed={called && !loading && !preview}
      saving={saving}
      error={error}
      onRetryPreview={requestPreview}
      onCancel={onCancel}
      onConfirm={onConfirm}
    />
  )
}

ApprovalDialog.propTypes = {
  rule: PropTypes.shape({
    trigger: PropTypes.string.isRequired,
    delayDays: PropTypes.number.isRequired,
    previousDelayDays: PropTypes.number,
  }).isRequired,
  sendSchedule: PropTypes.string.isRequired,
  holdoutPercent: PropTypes.number.isRequired,
  saving: PropTypes.bool,
  error: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
}
